import './styles/core.css';
import { createDefaultState } from './app/defaultState';
import { AppStore } from './app/store';
import { ERA_MANIFESTS, getEra } from './data/eras';
import { PRESET_DEFINITIONS, applyPresetDefinition, getPresetDefinition } from './data/presets';
import { calculateScientificFrame } from './science/scientificKernel';
import type {
  AccuracyMode,
  AnalysisView,
  AppState,
  BoundaryGeometry,
  ManipulatorLevel,
  ProductMode,
  BandShape,
  FieldComponent,
  QuantumState,
  ScientificFrame
} from './science/types';
import { createScene } from './rendering/createScene';
import { MiniPlots } from './ui/MiniPlots';
import { loadEraVisualPackage } from './eras/loader';
import type { EraId } from './science/types';

const qs = <T extends Element>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Required element not found: ${selector}`);
  return element;
};

const qsa = <T extends Element>(selector: string): T[] => Array.from(document.querySelectorAll<T>(selector));

const APP_VERSION = '1.0.0-rc1';
const SCHEMA_VERSION = '0.7';
const RELEASE_LABEL = 'research-release-candidate';

const announce = (message: string): void => {
  const region = document.querySelector<HTMLElement>('#app-status');
  if (!region) return;
  region.textContent = '';
  window.setTimeout(() => { region.textContent = message; }, 20);
};


const MODE_COPY: Record<ProductMode, { title: string; body: string }> = {
  explore: { title: 'Explore', body: 'Intuitive exploration with visible guidance and constrained controls.' },
  learn: { title: 'Learn', body: 'Guided explanations connect apparatus, history, equations, visual mappings, and variables.' },
  laboratory: { title: 'Laboratory', body: 'Full parameter access, scenario provenance, equations, data inspection, and export scaffolding.' }
};

const ACCURACY_LABELS: Record<AccuracyMode, string> = {
  conceptual: 'Conceptual',
  'experiment-matched': 'Experiment-Matched',
  mathematical: 'Mathematical',
  cinematic: 'Cinematic'
};

const BOUNDARY_ICONS: Record<BoundaryGeometry, string> = {
  none: '○',
  plates: 'Ⅱ',
  sphere: '◉',
  cavity: '▣',
  waveguide: '⌁',
  custom: '✧'
};

const BOUNDARY_LABELS: Record<BoundaryGeometry, string> = {
  none: 'None',
  plates: 'Plates',
  sphere: 'Sphere',
  cavity: 'Cavity',
  waveguide: 'Waveguide',
  custom: 'Custom'
};

const initialState = createDefaultState();
const requestedEra = new URLSearchParams(location.search).get('era') as EraId | null;
if (requestedEra && ERA_MANIFESTS.some((era) => era.id === requestedEra)) {
  initialState.era = requestedEra;
  applyPresetDefinition(initialState, getEra(requestedEra).defaultPreset);
}
const store = new AppStore(initialState);
const fieldScene = createScene(qs<HTMLElement>('#three-stage'), {
  onProbeSelect: (probe) => store.update((draft) => {
    draft.selectedProbe = probe;
    draft.visualization.sceneTool = 'probes';
  })
});
const miniPlots = new MiniPlots({
  onSpectrumFrequency: (value) => store.update((draft) => { draft.scientific.frequencyLog10 = value; draft.analysisView = 'spectrum'; }),
  onCorrelationX: (value) => store.update((draft) => {
    switch (draft.era) {
      case '2010s': draft.scientific.frequencyLog10 = Math.log10(Math.max(1, 10 ** draft.scientific.modulationFrequencyLog10 / 2 + value * 1e9)); break;
      case '1990s-2000s': draft.scientific.probeDelay = value * 1000; break;
      case '1970s-1980s': draft.scientific.integrationTimeLog10 = Math.log10(Math.max(1e-15, value * 1e-9)); break;
      case '1950s-1960s': draft.scientific.boundarySeparationLog10 = Math.log10(Math.max(1e-12, value * 1e-9)); break;
      case '1940s': draft.scientific.frequencyLog10 = Math.log10(Math.max(1, Math.abs(value) * 1e6)); break;
      case 'modern': default: draft.scientific.probeDelay = value; break;
    }
    draft.analysisView = 'correlations';
  }),
  onQuadratureAngle: (value) => store.update((draft) => { draft.scientific.squeezingAngle = value; draft.analysisView = 'quadratures'; }),
  onSliceProbe: (probe) => store.update((draft) => { draft.selectedProbe = probe; draft.analysisView = 'field'; })
});
let latestFrame: ScientificFrame = calculateScientificFrame(store.getState(), 0);
let simulationTime = 0;
let lastTickerTimestamp = 0;
let pendingSimulationStep = 0;
let lastFrameUpdate = 0;
let loadedVisualEra: EraId | null = null;
let eraLoadToken = 0;

const powerLabel = (log10: number, unit: string): string => {
  const rounded = Math.round(log10 * 10) / 10;
  const exact = Math.abs(rounded - Math.round(rounded)) < 0.001;
  return `10${exact ? superscript(Math.round(rounded)) : `^${rounded.toFixed(1)}`} ${unit}`;
};

const superscript = (value: number): string => {
  const map: Record<string, string> = { '-': '⁻', '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹' };
  return String(value).split('').map((char) => map[char] ?? char).join('');
};

const engineeringLength = (log10: number): string => {
  const value = 10 ** log10;
  if (value < 1e-9) return `${(value * 1e12).toPrecision(3)} pm`;
  if (value < 1e-6) return `${(value * 1e9).toPrecision(3)} nm`;
  if (value < 1e-3) return `${(value * 1e6).toPrecision(3)} μm`;
  if (value < 1) return `${(value * 1e3).toPrecision(3)} mm`;
  return `${value.toPrecision(3)} m`;
};

const engineeringTime = (log10: number): string => {
  const value = 10 ** log10;
  if (value < 1e-12) return `${(value * 1e15).toPrecision(3)} fs`;
  if (value < 1e-9) return `${(value * 1e12).toPrecision(3)} ps`;
  if (value < 1e-6) return `${(value * 1e9).toPrecision(3)} ns`;
  if (value < 1e-3) return `${(value * 1e6).toPrecision(3)} μs`;
  return `${value.toPrecision(3)} s`;
};

const frequencyLabel = (log10: number): string => {
  const value = 10 ** log10;
  if (value >= 1e12) return `${(value / 1e12).toPrecision(3)} THz`;
  if (value >= 1e9) return `${(value / 1e9).toPrecision(3)} GHz`;
  if (value >= 1e6) return `${(value / 1e6).toPrecision(3)} MHz`;
  return `${value.toPrecision(3)} Hz`;
};

const formatScientific = (value: number, unit: string, digits = 4): string => `${value.toExponential(digits)} ${unit}`;

const fieldComponentLabel = (component: FieldComponent): string => component === 'magnitude' ? '|E|' : `E${component}`;

const downloadBlob = (filename: string, blob: Blob): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1200);
};

const setActive = (elements: Element[], predicate: (element: Element) => boolean): void => {
  elements.forEach((element) => {
    const active = predicate(element);
    element.classList.toggle('is-active', active);
    if (element instanceof HTMLButtonElement) {
      element.setAttribute('aria-pressed', String(active));
      if (element.getAttribute('role') === 'tab' || element.classList.contains('era-button')) element.setAttribute('aria-selected', String(active));
    }
  });
};

const renderEraSelector = (): void => {
  const host = qs<HTMLElement>('#era-selector');
  host.setAttribute('role', 'tablist');
  host.replaceChildren(...ERA_MANIFESTS.map((era) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'era-button';
    button.dataset.era = era.id;
    button.setAttribute('role', 'tab');
    button.setAttribute('aria-label', `${era.label}: ${era.subtitle}`);
    button.innerHTML = `<strong>${era.label}</strong><span>${era.subtitle}</span>`;
    button.addEventListener('click', () => {
      store.update((draft) => {
        draft.era = era.id;
        applyPresetDefinition(draft, era.defaultPreset);
        if (!era.availableBoundaries.includes(draft.scientific.boundaryGeometry)) {
          draft.scientific.boundaryGeometry = era.availableBoundaries[0] ?? 'none';
        }
        if (era.id === '1940s') {
          draft.scientific.state = 'vacuum';
          draft.scientific.boundaryModulation = false;
        }
      });
      announce(`${era.label} loaded: ${era.subtitle}.`);
    });
    return button;
  }));
};

const renderBoundaryOptions = (state: Readonly<AppState>): void => {
  const host = qs<HTMLElement>('#boundary-options');
  const era = getEra(state.era);
  host.replaceChildren(...era.availableBoundaries.map((boundary) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'boundary-button';
    button.dataset.boundary = boundary;
    button.innerHTML = `<i>${BOUNDARY_ICONS[boundary]}</i><span>${BOUNDARY_LABELS[boundary]}</span>`;
    button.classList.toggle('is-active', boundary === state.scientific.boundaryGeometry);
    button.setAttribute('aria-pressed', String(boundary === state.scientific.boundaryGeometry));
    button.addEventListener('click', () => store.update((draft) => { draft.scientific.boundaryGeometry = boundary; }));
    return button;
  }));
};

const renderPresets = (state: Readonly<AppState>): void => {
  const presets = PRESET_DEFINITIONS[state.era];
  const select = qs<HTMLSelectElement>('#preset-select');
  select.replaceChildren(...presets.map((preset) => {
    const option = document.createElement('option');
    option.value = preset.label;
    option.textContent = preset.label;
    option.selected = preset.label === state.preset;
    return option;
  }));
  const list = qs<HTMLElement>('#experiment-list');
  list.replaceChildren(...presets.map((preset) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'preset-item';
    button.textContent = preset.label;
    button.title = preset.note;
    button.classList.toggle('is-active', preset.label === state.preset);
    button.setAttribute('aria-pressed', String(preset.label === state.preset));
    button.addEventListener('click', () => store.update((draft) => applyPresetDefinition(draft, preset.label)));
    return button;
  }));
};

const loadEraPresentation = (eraId: EraId): void => {
  if (loadedVisualEra === eraId) return;
  const token = ++eraLoadToken;
  document.body.dataset.eraLoading = 'true';
  void loadEraVisualPackage(eraId).then((profile) => {
    if (token !== eraLoadToken || store.getState().era !== eraId) return;
    loadedVisualEra = eraId;
    document.documentElement.dataset.eraPackage = profile.skin;
    document.body.dataset.eraLoading = 'false';
    document.body.className = document.body.className.replace(/era-instrument-[\w-]+/g, '').trim();
    document.body.classList.add(profile.instrumentClass);
    document.documentElement.style.setProperty('--plot-bg-top', profile.plotBackgroundTop);
    document.documentElement.style.setProperty('--plot-bg-bottom', profile.plotBackgroundBottom);
    document.documentElement.style.setProperty('--plot-grid', profile.plotGrid);
    document.documentElement.style.setProperty('--plot-sweep', profile.plotSweep);
    const illustration = qs<HTMLElement>('#apparatus-illustration');
    illustration.innerHTML = profile.apparatusMarkup;
    illustration.dataset.era = profile.id;
    qs('#apparatus-name').textContent = profile.apparatusTitle;
    qs('#visualizer-heading').textContent = `${profile.apparatusTitle}: field and correlation view`;
    qs('#apparatus-description').textContent = profile.apparatusCaption;
    qs('#era-narrative-title').textContent = profile.narrativeTitle;
    qs('#era-narrative-body').textContent = profile.narrativeBody;
    qs('#era-historical-note').textContent = profile.historicalNote;
    fieldScene.setVisualProfile?.(profile);
    miniPlots.update(latestFrame, store.getState());
  }).catch((error) => {
    document.body.dataset.eraLoading = 'error';
    console.error('Unable to load era visual package.', error);
  });
};

const applyEra = (state: Readonly<AppState>): void => {
  const era = getEra(state.era);
  loadEraPresentation(era.id);
  document.documentElement.dataset.era = era.id;
  document.documentElement.style.setProperty('--accent', era.accent);
  document.documentElement.style.setProperty('--accent-soft', era.accentSoft);
  document.documentElement.style.setProperty('--panel', era.panel);
  document.documentElement.style.setProperty('--panel-raised', era.panelRaised);
  document.documentElement.style.setProperty('--screen', era.screen);
  document.documentElement.style.setProperty('--metal', era.metal);
  document.documentElement.style.setProperty('--slice-hue', era.id === '1990s-2000s' ? '82' : era.id === '1970s-1980s' || era.id === '1950s-1960s' || era.id === '1940s' ? '30' : '205');
  qs('#era-kicker').textContent = era.subtitle;
  qs('#era-description').textContent = era.description;
  qs('#stage-era-badge').textContent = era.label;
  qs('#stage-field-badge').textContent = state.era === '2010s' ? 'Dynamical Casimir Circuit' : state.era === '1990s-2000s' ? 'Squeezed Vacuum / Homodyne' : state.era === '1970s-1980s' ? 'Parametric Dynamic Boundary' : state.era === '1950s-1960s' ? 'Static Casimir / Casimir-Polder' : state.era === '1940s' ? 'Lamb Shift Foundations' : 'Electromagnetic Vacuum';
  qs<HTMLElement>('#apparatus-illustration').dataset.era = state.era;
  setActive(qsa('.era-button'), (element) => (element as HTMLElement).dataset.era === era.id);
};

const renderControlState = (state: Readonly<AppState>): void => {
  const assignRange = (id: string, value: number): void => { qs<HTMLInputElement>(id).value = String(value); };
  assignRange('#frequency', state.scientific.frequencyLog10);
  assignRange('#spatial-scale', state.scientific.spatialLog10);
  assignRange('#time-scale', state.scientific.timeLog10);
  assignRange('#temperature', state.scientific.temperature);
  qs<HTMLSelectElement>('#state-select').value = state.scientific.state;
  qs<HTMLSelectElement>('#field-component').value = state.scientific.fieldComponent;
  qs<HTMLSelectElement>('#band-shape').value = state.scientific.bandShape;
  assignRange('#squeezing', state.scientific.squeezingDb);
  assignRange('#squeezing-angle', state.scientific.squeezingAngle);
  assignRange('#visual-gain', state.visualization.gain);
  assignRange('#detector-bandwidth', state.scientific.detectorBandwidthLog10);
  assignRange('#detector-efficiency', state.scientific.detectorEfficiency);
  assignRange('#integration-time', state.scientific.integrationTimeLog10);
  assignRange('#probe-separation', state.scientific.probeSeparation);
  assignRange('#probe-delay', state.scientific.probeDelay);
  assignRange('#boundary-separation', state.scientific.boundarySeparationLog10);
  assignRange('#cavity-resonance', state.scientific.cavityResonanceLog10);
  qs<HTMLInputElement>('#boundary-modulation').checked = state.scientific.boundaryModulation;
  assignRange('#modulation-frequency', state.scientific.modulationFrequencyLog10);
  assignRange('#modulation-amplitude', state.scientific.modulationAmplitude);
  assignRange('#numerical-resolution', state.scientific.numericalResolution);
  assignRange('#phase-velocity', state.scientific.phaseVelocityFraction);
  assignRange('#quality-factor', state.scientific.qualityFactor);
  assignRange('#effective-length', state.scientific.effectiveLengthLog10);
  assignRange('#polarizability-volume', state.scientific.polarizabilityVolumeLog10);
  assignRange('#atomic-number', state.scientific.atomicNumber);
  assignRange('#cutoff-ratio', state.scientific.cutoffRatio);
  qs<HTMLInputElement>('#random-seed').value = String(state.scientific.randomSeed);
  qs<HTMLInputElement>('#field-ontology').checked = state.visualization.fieldOntology;
  qs<HTMLInputElement>('#virtual-particle').checked = state.visualization.virtualParticleView;
  qs<HTMLInputElement>('#vectors-visible').checked = state.visualization.vectorsVisible;
  qs<HTMLInputElement>('#correlations-visible').checked = state.visualization.correlationsVisible;
  qs<HTMLInputElement>('#probes-visible').checked = state.visualization.probesVisible;
  qs<HTMLInputElement>('#reduced-motion').checked = state.visualization.reducedMotion;
  qs<HTMLSelectElement>('#animation-speed').value = String(state.visualization.animationSpeed);

  qs('#frequency-out').textContent = frequencyLabel(state.scientific.frequencyLog10);
  qs('#spatial-scale-out').textContent = engineeringLength(state.scientific.spatialLog10);
  qs('#time-scale-out').textContent = engineeringTime(state.scientific.timeLog10);
  qs('#temperature-out').textContent = `${state.scientific.temperature.toFixed(state.scientific.temperature < 10 ? 2 : 1)} K`;
  qs('#squeezing-out').textContent = `${state.scientific.squeezingDb.toFixed(1)} dB`;
  qs('#squeezing-angle-out').textContent = `${state.scientific.squeezingAngle.toFixed(0)}°`;
  qs('#visual-gain-out').textContent = `${state.visualization.gain.toFixed(1)}×`;
  qs('#detector-bandwidth-out').textContent = frequencyLabel(state.scientific.detectorBandwidthLog10);
  qs('#detector-efficiency-out').textContent = `${(state.scientific.detectorEfficiency * 100).toFixed(0)}%`;
  qs('#integration-time-out').textContent = engineeringTime(state.scientific.integrationTimeLog10);
  qs('#probe-separation-out').textContent = `${state.scientific.probeSeparation.toFixed(3)} mm`;
  qs('#probe-separation-label').textContent = `separation = ${state.scientific.probeSeparation.toFixed(3)} mm · Δt = ${state.scientific.probeDelay.toFixed(3)} ps`;
  qs('.probe-readout strong').textContent = `Probe ${state.selectedProbe} · ${fieldComponentLabel(state.scientific.fieldComponent)}`;
  qs('#probe-delay-out').textContent = `${state.scientific.probeDelay.toFixed(3)} ps`;
  qs('#boundary-separation-out').textContent = engineeringLength(state.scientific.boundarySeparationLog10);
  qs('#cavity-resonance-out').textContent = frequencyLabel(state.scientific.cavityResonanceLog10);
  qs('#modulation-frequency-out').textContent = frequencyLabel(state.scientific.modulationFrequencyLog10);
  qs('#modulation-amplitude-out').textContent = state.scientific.modulationAmplitude.toFixed(3);
  qs('#numerical-resolution-out').textContent = `${state.scientific.numericalResolution} modes`;
  qs('#phase-velocity-out').textContent = `${state.scientific.phaseVelocityFraction.toFixed(2)} c`;
  qs('#quality-factor-out').textContent = state.scientific.qualityFactor.toPrecision(4);
  qs('#effective-length-out').textContent = engineeringLength(state.scientific.effectiveLengthLog10);
  qs('#polarizability-volume-out').textContent = `${(10 ** state.scientific.polarizabilityVolumeLog10).toExponential(3)} m³`;
  qs('#atomic-number-out').textContent = String(Math.round(state.scientific.atomicNumber));
  qs('#cutoff-ratio-out').textContent = state.scientific.cutoffRatio.toPrecision(5);
  qsa<HTMLElement>('.adapter-control').forEach((row) => {
    const scope = (row.dataset.eraScope ?? '').split(',');
    row.hidden = !scope.includes(state.era);
  });
  const animationButton = qs<HTMLButtonElement>('#animation-toggle');
  animationButton.textContent = state.visualization.animationPaused ? 'Resume' : 'Pause';
  animationButton.setAttribute('aria-pressed', String(state.visualization.animationPaused));
  qs('#stage-release-badge').textContent = state.visualization.animationPaused ? 'Phase 7 · Paused' : 'Phase 7 · Live';
};

const renderModeState = (state: Readonly<AppState>): void => {
  setActive(qsa('[data-product-mode]'), (element) => (element as HTMLElement).dataset.productMode === state.productMode);
  setActive(qsa('[data-manipulator-level]'), (element) => (element as HTMLElement).dataset.manipulatorLevel === state.manipulatorLevel);
  setActive(qsa('.control-group'), (element) => element.id === `control-${state.manipulatorLevel}`);
  setActive(qsa('[data-view]'), (element) => (element as HTMLElement).dataset.view === state.analysisView);
  setActive(qsa('[data-scene-tool]'), (element) => (element as HTMLElement).dataset.sceneTool === state.visualization.sceneTool);
  qsa<HTMLInputElement>('input[name="accuracy"]').forEach((input) => { input.checked = input.value === state.accuracyMode; });
  qs('#stage-mode-badge').textContent = ACCURACY_LABELS[state.accuracyMode];
  qs('#current-mode-title').textContent = MODE_COPY[state.productMode].title;
  qs('#current-mode-description').textContent = MODE_COPY[state.productMode].body;
  document.body.dataset.productMode = state.productMode;
  document.body.dataset.analysisView = state.analysisView;
  document.body.dataset.animationPaused = String(state.visualization.animationPaused);
};

const renderInspection = (state: Readonly<AppState>, frame: ScientificFrame): void => {
  const list = qs<HTMLDListElement>('#data-inspection');
  const observable = frame.observables;
  const entries: Array<[string, string]> = [
    ['Model', frame.modelId],
    ['Validation', frame.validation.status],
    ['Era package', document.documentElement.dataset.eraPackage ?? 'loading'],
    ['Renderer / tier', `${qs<HTMLElement>('#three-stage').dataset.renderer ?? 'loading'} / ${qs<HTMLElement>('#three-stage').dataset.renderTier ?? 'automatic'}`],
    ['Probe / component', `${state.selectedProbe} / ${fieldComponentLabel(state.scientific.fieldComponent)}`]
  ];
  if (state.era === 'modern') {
    entries.push(
      ['Component E RMS', formatScientific(observable.electricComponentRmsVPerM, 'V/m')],
      ['Magnitude E RMS', formatScientific(observable.electricMagnitudeRmsVPerM, 'V/m')],
      ['Magnetic RMS', formatScientific(observable.magneticRmsTesla, 'T')],
      ['Energy density', formatScientific(observable.energyDensityJPerM3, 'J/m³')],
      ['Correlation', formatScientific(observable.correlationV2PerM2, 'V²/m²')],
      ['Correlation coefficient', observable.correlationCoefficient.toFixed(6)],
      ['Detector RMS', formatScientific(observable.detectorRmsVPerM, 'V/m')]
    );
  } else {
    frame.adapterMetrics.forEach((item) => entries.push([item.label, `${Number.isFinite(item.value) ? item.value.toExponential(4) : 'not finite'} ${item.unit}`]));
    entries.push(
      ['Display-field E RMS', formatScientific(observable.electricComponentRmsVPerM, 'V/m')],
      ['Display-field energy density', formatScientific(observable.energyDensityJPerM3, 'J/m³')]
    );
  }
  if (observable.casimirPressurePa !== undefined) entries.push(['Casimir pressure', formatScientific(observable.casimirPressurePa, 'Pa')]);
  if (observable.casimirEnergyPerAreaJPerM2 !== undefined) entries.push(['Casimir E/A', formatScientific(observable.casimirEnergyPerAreaJPerM2, 'J/m²')]);
  entries.push(['Numerical error', `${frame.validation.relativeIntegrationError.toExponential(2)} relative`]);
  if (frame.uncertainty) {
    entries.push(
      ['Quantified uncertainty', `${frame.uncertainty.combinedQuantifiedRelative.toExponential(2)} relative`],
      ['Statistical term', frame.uncertainty.statisticalRelative === null ? 'not assigned' : `${frame.uncertainty.statisticalRelative.toExponential(2)} relative`],
      ['Model discrepancy', frame.uncertainty.modelDiscrepancyQuantified ? 'quantified' : 'not quantified']
    );
  }
  if (frame.academicReview) entries.push(['Academic review', `${frame.academicReview.status} · ${frame.academicReview.auditId}`]);
  list.replaceChildren(...entries.flatMap(([term, definition]) => {
    const dt = document.createElement('dt');
    dt.textContent = term;
    const dd = document.createElement('dd');
    dd.textContent = definition;
    return [dt, dd];
  }));
};

const renderScientificContext = (state: Readonly<AppState>, frame: ScientificFrame): void => {
  const preset = getPresetDefinition(state.era, state.preset);
  const equation = qs<HTMLElement>('#equation-content');
  const equations = frame.equations.map((item) => `
    <p class="equation-label">${escapeHtml(item.label)}</p>
    <div class="typeset-equation">${item.html}</div>
    <small>Sources: ${escapeHtml(item.sourceIds.join(', '))}</small>
  `).join('');
  const activeMetrics = frame.adapterMetrics.slice(0, 6).map((item) => `<span>${escapeHtml(item.label)} = ${escapeHtml(Number.isFinite(item.value) ? item.value.toExponential(4) : 'not finite')} ${escapeHtml(item.unit)}</span>`).join('');
  equation.innerHTML = `${equations}<div class="equation-substitution"><strong>Active numerical outputs</strong>${activeMetrics}</div><p>${escapeHtml(frame.validation.scope)}</p>`;
  qs('#reference-summary').textContent = `${preset?.note ?? 'Versioned scientific adapter.'} Validation: ${frame.validation.status}. Review: ${frame.academicReview?.status ?? 'pending'}. Sources: ${frame.validation.sourceIds.join(', ')}.`;
  qs('.prototype-warning').innerHTML = `<strong>Phase 7 research-release candidate.</strong> ${escapeHtml(frame.warning)} Quantified uncertainty covers only declared numerical and ideal-sampling terms; model discrepancy and apparatus systematics remain unquantified unless explicitly stated.`;
  qs('#method-summary').textContent = `${frame.validation.scope} ${frame.uncertainty?.coverage ?? ''} ${frame.renderingMapping}`;
  const longDescription = `${frame.plotSemantics.spectrumTitle}, ${frame.plotSemantics.quadratureTitle}, ${frame.plotSemantics.correlationTitle}, ${frame.plotSemantics.detectorTitle}, and ${frame.plotSemantics.sliceTitle} are synchronized with model ${frame.modelId}. Validation status is ${frame.validation.status}. ${frame.renderingMapping}`;
  qs('#visualizer-long-description').textContent = longDescription;
  qs('#three-stage').setAttribute('aria-label', `${getEra(state.era).label}. ${frame.modelId}. ${frame.validation.status}. Interactive field and apparatus visualization; use the plots and Data Inspection for quantitative values.`);
  const titleMap: Array<[string, string]> = [
    ['#spectrum-title', `${frame.plotSemantics.spectrumTitle} <span id="spectrum-subtitle">${escapeHtml(frame.plotSemantics.spectrumSubtitle)}</span>`],
    ['#quadrature-title', `${frame.plotSemantics.quadratureTitle} <span>state space</span>`],
    ['#correlation-title', `${frame.plotSemantics.correlationTitle} <span>interactive x-axis</span>`],
    ['#detector-title', `${frame.plotSemantics.detectorTitle} <span>reproducible samples</span>`],
    ['#slice-title', `${frame.plotSemantics.sliceTitle} <span>animated map</span>`]
  ];
  titleMap.forEach(([selector, html]) => { qs<HTMLElement>(selector).innerHTML = html; });
};

const renderAll = (state: Readonly<AppState>): void => {
  applyEra(state);
  renderPresets(state);
  renderBoundaryOptions(state);
  renderControlState(state);
  renderModeState(state);
  latestFrame = calculateScientificFrame(state, simulationTime);
  fieldScene.update(state, latestFrame);
  miniPlots.update(latestFrame, state);
  renderInspection(state, latestFrame);
  renderScientificContext(state, latestFrame);
};

const bindRange = (selector: string, setter: (state: AppState, value: number) => void): void => {
  qs<HTMLInputElement>(selector).addEventListener('input', (event) => {
    const value = Number((event.currentTarget as HTMLInputElement).value);
    store.update((draft) => setter(draft, value));
  });
};

const bindCheckbox = (selector: string, setter: (state: AppState, checked: boolean) => void): void => {
  qs<HTMLInputElement>(selector).addEventListener('change', (event) => {
    const checked = (event.currentTarget as HTMLInputElement).checked;
    store.update((draft) => setter(draft, checked));
  });
};

const bindControls = (): void => {
  qsa<HTMLButtonElement>('[data-product-mode]').forEach((button) => button.addEventListener('click', () => store.update((draft) => {
    const mode = button.dataset.productMode as ProductMode;
    draft.productMode = mode;
    draft.manipulatorLevel = mode === 'explore' ? 'essential' : mode === 'learn' ? 'experimental' : 'advanced';
  })));
  qsa<HTMLButtonElement>('[data-manipulator-level]').forEach((button) => button.addEventListener('click', () => store.update((draft) => { draft.manipulatorLevel = button.dataset.manipulatorLevel as ManipulatorLevel; })));
  qsa<HTMLButtonElement>('[data-view]').forEach((button) => button.addEventListener('click', () => store.update((draft) => { draft.analysisView = button.dataset.view as AnalysisView; })));
  qsa<HTMLInputElement>('input[name="accuracy"]').forEach((input) => input.addEventListener('change', () => { if (input.checked) store.update((draft) => { draft.accuracyMode = input.value as AccuracyMode; }); }));
  qsa<HTMLButtonElement>('[data-scene-tool]').forEach((button) => button.addEventListener('click', () => {
    const tool = button.dataset.sceneTool ?? 'orbit';
    if (tool === 'cones') {
      const enable = !store.getState().visualization.lightConesVisible;
      fieldScene.setTool(enable ? 'cones' : 'orbit');
      store.update((draft) => {
        draft.visualization.lightConesVisible = enable;
        draft.visualization.sceneTool = enable ? 'cones' : 'orbit';
      });
      return;
    }
    fieldScene.setTool(tool);
    store.update((draft) => { draft.visualization.sceneTool = tool as AppState['visualization']['sceneTool']; });
  }));

  bindRange('#frequency', (draft, value) => { draft.scientific.frequencyLog10 = value; });
  bindRange('#spatial-scale', (draft, value) => { draft.scientific.spatialLog10 = value; });
  bindRange('#time-scale', (draft, value) => { draft.scientific.timeLog10 = value; });
  bindRange('#temperature', (draft, value) => { draft.scientific.temperature = value; });
  bindRange('#squeezing', (draft, value) => { draft.scientific.squeezingDb = value; });
  bindRange('#squeezing-angle', (draft, value) => { draft.scientific.squeezingAngle = value; });
  bindRange('#visual-gain', (draft, value) => { draft.visualization.gain = value; });
  bindRange('#detector-bandwidth', (draft, value) => { draft.scientific.detectorBandwidthLog10 = value; });
  bindRange('#detector-efficiency', (draft, value) => { draft.scientific.detectorEfficiency = value; });
  bindRange('#integration-time', (draft, value) => { draft.scientific.integrationTimeLog10 = value; });
  bindRange('#probe-separation', (draft, value) => { draft.scientific.probeSeparation = value; });
  bindRange('#probe-delay', (draft, value) => { draft.scientific.probeDelay = value; });
  bindRange('#boundary-separation', (draft, value) => { draft.scientific.boundarySeparationLog10 = value; });
  bindRange('#cavity-resonance', (draft, value) => { draft.scientific.cavityResonanceLog10 = value; });
  bindRange('#modulation-frequency', (draft, value) => { draft.scientific.modulationFrequencyLog10 = value; });
  bindRange('#modulation-amplitude', (draft, value) => { draft.scientific.modulationAmplitude = value; });
  bindRange('#numerical-resolution', (draft, value) => { draft.scientific.numericalResolution = value; });
  bindRange('#phase-velocity', (draft, value) => { draft.scientific.phaseVelocityFraction = value; });
  bindRange('#quality-factor', (draft, value) => { draft.scientific.qualityFactor = value; });
  bindRange('#effective-length', (draft, value) => { draft.scientific.effectiveLengthLog10 = value; });
  bindRange('#polarizability-volume', (draft, value) => { draft.scientific.polarizabilityVolumeLog10 = value; });
  bindRange('#atomic-number', (draft, value) => { draft.scientific.atomicNumber = Math.round(value); });
  bindRange('#cutoff-ratio', (draft, value) => { draft.scientific.cutoffRatio = value; });

  bindCheckbox('#field-ontology', (draft, checked) => { draft.visualization.fieldOntology = checked; });
  bindCheckbox('#virtual-particle', (draft, checked) => { draft.visualization.virtualParticleView = checked; });
  bindCheckbox('#boundary-modulation', (draft, checked) => { draft.scientific.boundaryModulation = checked; });
  bindCheckbox('#vectors-visible', (draft, checked) => { draft.visualization.vectorsVisible = checked; });
  bindCheckbox('#correlations-visible', (draft, checked) => { draft.visualization.correlationsVisible = checked; });
  bindCheckbox('#probes-visible', (draft, checked) => { draft.visualization.probesVisible = checked; });
  bindCheckbox('#reduced-motion', (draft, checked) => { draft.visualization.reducedMotion = checked; });

  qs<HTMLSelectElement>('#state-select').addEventListener('change', (event) => store.update((draft) => { draft.scientific.state = (event.currentTarget as HTMLSelectElement).value as QuantumState; }));
  qs<HTMLSelectElement>('#field-component').addEventListener('change', (event) => store.update((draft) => { draft.scientific.fieldComponent = (event.currentTarget as HTMLSelectElement).value as FieldComponent; }));
  qs<HTMLSelectElement>('#band-shape').addEventListener('change', (event) => store.update((draft) => { draft.scientific.bandShape = (event.currentTarget as HTMLSelectElement).value as BandShape; }));
  qs<HTMLSelectElement>('#preset-select').addEventListener('change', (event) => store.update((draft) => applyPresetDefinition(draft, (event.currentTarget as HTMLSelectElement).value)));
  qs<HTMLInputElement>('#random-seed').addEventListener('change', (event) => store.update((draft) => {
    const value = Number((event.currentTarget as HTMLInputElement).value);
    draft.scientific.randomSeed = Number.isFinite(value) && value > 0 ? Math.round(value) : 1;
  }));

  qs('#animation-toggle').addEventListener('click', () => store.update((draft) => { draft.visualization.animationPaused = !draft.visualization.animationPaused; announce(draft.visualization.animationPaused ? 'Animation paused.' : 'Animation resumed.'); }));
  qs('#animation-step').addEventListener('click', () => {
    pendingSimulationStep += 1 / 15;
    fieldScene.step(1 / 15);
    miniPlots.step(1 / 15);
    const state = store.getState();
    latestFrame = calculateScientificFrame(state, simulationTime + pendingSimulationStep);
    fieldScene.update(state, latestFrame);
    miniPlots.update(latestFrame, state);
    renderInspection(state, latestFrame);
    announce('Animation advanced by one scientific display step.');
  });
  qs<HTMLSelectElement>('#animation-speed').addEventListener('change', (event) => store.update((draft) => {
    const value = Number((event.currentTarget as HTMLSelectElement).value);
    draft.visualization.animationSpeed = Number.isFinite(value) ? Math.max(0.1, Math.min(3, value)) : 1;
  }));
  qs('#reset-view').addEventListener('click', () => fieldScene.setTool('center'));
  qs('#take-snapshot').addEventListener('click', exportImage);
  qs('#export-image').addEventListener('click', exportImage);
  qs('#randomize').addEventListener('click', randomizeScenario);
  qs('#save-scenario').addEventListener('click', saveScenario);
  qs('#load-scenario').addEventListener('click', () => qs<HTMLInputElement>('#scenario-file').click());
  qs<HTMLInputElement>('#scenario-file').addEventListener('change', loadScenarioFile);
  qs('#export-json').addEventListener('click', exportScenario);
  qs('#export-data').addEventListener('click', exportData);
  qs('#generate-pdf').addEventListener('click', () => { void generatePdfReport(); });
  qs('#copy-data').addEventListener('click', copyInspection);
  qs('#favorite-preset').addEventListener('click', (event) => { (event.currentTarget as HTMLButtonElement).textContent = (event.currentTarget as HTMLButtonElement).textContent === '★' ? '☆' : '★'; });
  qs('#view-reference').addEventListener('click', showReferences);
  qs('#view-model-audit').addEventListener('click', () => { void showModelAudit(); });
  qs('#view-release-audit').addEventListener('click', () => { void showReleaseAudit(); });
  qs('#view-accessibility').addEventListener('click', () => { void showAccessibilityAudit(); });
  qs('#view-shortcuts').addEventListener('click', showKeyboardHelp);
  qs('#view-method').addEventListener('click', showMethod);
  qs('#methodological-note').addEventListener('click', showMethod);
  qs('#view-derivation').addEventListener('click', showDerivation);
};

const randomizeScenario = (): void => {
  store.update((draft) => {
    draft.scientific.randomSeed = Math.floor(1 + Math.random() * 2_000_000_000);
    draft.scientific.squeezingAngle = Math.round(Math.random() * 180);
    draft.scientific.probeDelay = Math.round((Math.random() * 20 - 10) * 100) / 100;
    draft.scientific.probeSeparation = Math.round((1 + Math.random() * 12) * 100) / 100;
    draft.visualization.gain = Math.round((1 + Math.random() * 6) * 10) / 10;
  });
};

const scenarioManifest = (): object => {
  const currentState = store.getState();
  const activePreset = getPresetDefinition(currentState.era, currentState.preset);
  return {
  application: 'Stella Nova Quantum Vacuum Fluctuation Visualizer',
  applicationVersion: APP_VERSION,
  releaseLabel: RELEASE_LABEL,
  createdAt: new Date().toISOString(),
  numericalBackend: 'deterministic-typescript-cpu',
  benchmarkRegistry: './data/benchmarks.json',
  sourceRegistry: './data/references.json',
  modelAuditRegistry: './data/model-audit.json',
  accessibilityAudit: './data/accessibility-audit.json',
  releaseAudit: './data/release-audit.json',
  state: currentState,
  activePreset: activePreset ? {
    label: activePreset.label,
    classification: activePreset.validation,
    note: activePreset.note,
    sourceIds: activePreset.sourceIds
  } : null,
  presentation: {
    eraPackage: document.documentElement.dataset.eraPackage ?? null,
    renderer: qs<HTMLElement>('#three-stage').dataset.renderer ?? 'loading',
    renderTier: qs<HTMLElement>('#three-stage').dataset.renderTier ?? 'automatic',
    goldenReference: `docs/references/${String(ERA_MANIFESTS.findIndex((era) => era.id === currentState.era) + 1).padStart(2, '0')}-${currentState.era === 'modern' ? 'modern-2020s' : currentState.era === '1940s' ? '1940s-foundations' : currentState.era}.png`,
    visualGain: currentState.visualization.gain,
    accuracyMode: currentState.accuracyMode
  },
  model: {
    id: latestFrame.modelId,
    version: latestFrame.modelVersion,
    warning: latestFrame.warning,
    frequencyBand: latestFrame.frequencyBand,
    observables: latestFrame.observables,
    adapterId: latestFrame.adapterId,
    adapterMetrics: latestFrame.adapterMetrics,
    equations: latestFrame.equations,
    plotSemantics: latestFrame.plotSemantics,
    renderingMapping: latestFrame.renderingMapping,
    validation: latestFrame.validation,
    uncertainty: latestFrame.uncertainty,
    academicReview: latestFrame.academicReview,
    seed: latestFrame.seed,
    physicalTimeSeconds: latestFrame.physicalTimeSeconds
  }
};
};

const exportScenario = (): void => {
  announce('Scenario JSON export prepared.');
  downloadBlob('qvfv-phase7-scenario.json', new Blob([JSON.stringify(scenarioManifest(), null, 2)], { type: 'application/json' }));
};

const saveScenario = (): void => {
  localStorage.setItem('qvfv-phase7-scenario', JSON.stringify(store.getState()));
  announce('Scenario saved in this browser.');
  showInfo('Scenario Saved', '<p>The current Phase 7 state has been saved in this browser. The portable JSON export includes model version, SI observables, assumptions, source identifiers, seed, and numerical-error estimates.</p>');
};

const isAppState = (value: unknown): value is AppState => {
  if (!value || typeof value !== 'object') return false;
  const state = value as Partial<AppState>;
  return state.schemaVersion === SCHEMA_VERSION && state.releaseLabel === RELEASE_LABEL && typeof state.era === 'string' && typeof state.scientific === 'object' && typeof state.visualization === 'object';
};

const migrateAppState = (value: unknown): AppState | null => {
  if (isAppState(value)) return value;
  if (!value || typeof value !== 'object') return null;
  const legacy = value as Record<string, unknown>;
  if (!['0.1', '0.2', '0.3', '0.4', '0.5', '0.6'].includes(String(legacy.schemaVersion))) return null;
  const defaults = createDefaultState();
  const legacyVisualization = typeof legacy.visualization === 'object' && legacy.visualization ? legacy.visualization as Partial<AppState['visualization']> : {};
  const legacyScientific = typeof legacy.scientific === 'object' && legacy.scientific ? legacy.scientific as Partial<AppState['scientific']> : {};
  const legacyState = legacy as unknown as Partial<AppState>;
  return {
    ...defaults,
    ...legacyState,
    schemaVersion: '0.7',
    releaseLabel: RELEASE_LABEL,
    scientific: { ...defaults.scientific, ...legacyScientific },
    visualization: { ...defaults.visualization, ...legacyVisualization, animationPaused: legacyVisualization.animationPaused ?? false, animationSpeed: legacyVisualization.animationSpeed ?? 1, sceneTool: legacyVisualization.sceneTool ?? 'orbit' }
  };
};

const loadScenarioFile = async (event: Event): Promise<void> => {
  const input = event.currentTarget as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  try {
    const parsed = JSON.parse(await file.text()) as unknown;
    const rawCandidate = isAppState(parsed) ? parsed : (parsed as { state?: unknown }).state ?? parsed;
    const candidate = migrateAppState(rawCandidate);
    if (!candidate) throw new Error('This file does not contain a compatible Phase 1 through Phase 7 scenario state.');
    store.replace(candidate);
    announce('Scenario loaded and migrated to schema 0.7.');
    showInfo('Scenario Loaded', '<p>The scenario was restored and migrated to the Phase 7 scientific schema where necessary.</p>');
  } catch (error) {
    showInfo('Scenario Load Error', `<p>${escapeHtml(error instanceof Error ? error.message : 'Unknown scenario error.')}</p>`);
  } finally {
    input.value = '';
  }
};

const exportData = (): void => {
  const observable = latestFrame.observables;
  const rows: Array<Array<string | number>> = [
    ['section', 'metric', 'value', 'unit', 'validation'],
    ['summary', 'electric_component_rms', observable.electricComponentRmsVPerM, 'V/m', latestFrame.validation.status],
    ['summary', 'electric_magnitude_rms', observable.electricMagnitudeRmsVPerM, 'V/m', latestFrame.validation.status],
    ['summary', 'magnetic_rms', observable.magneticRmsTesla, 'T', latestFrame.validation.status],
    ['summary', 'energy_density', observable.energyDensityJPerM3, 'J/m^3', latestFrame.validation.status],
    ['summary', 'mean_photon_occupation', observable.meanPhotonOccupation, 'dimensionless', latestFrame.validation.status],
    ['summary', 'correlation', observable.correlationV2PerM2, 'V^2/m^2', latestFrame.validation.status],
    ['summary', 'correlation_coefficient', observable.correlationCoefficient, 'dimensionless', latestFrame.validation.status],
    ['summary', 'detector_rms', observable.detectorRmsVPerM, 'V/m', latestFrame.validation.status],
    ['summary', 'detector_standard_error', observable.detectorStandardErrorVPerM, 'V/m', latestFrame.validation.status],
    ['summary', 'relative_integration_error', latestFrame.validation.relativeIntegrationError, 'dimensionless', 'numerical convergence estimate'],
    ['uncertainty', 'combined_quantified_relative', latestFrame.uncertainty?.combinedQuantifiedRelative ?? '', 'dimensionless', 'numerical and ideal-sampling terms only'],
    ['uncertainty', 'statistical_relative', latestFrame.uncertainty?.statisticalRelative ?? '', 'dimensionless', 'not assigned for idealized historical adapters'],
    ['audit', 'academic_review_status', latestFrame.academicReview?.status ?? 'pending', '', latestFrame.academicReview?.auditId ?? '']
  ];
  if (observable.casimirPressurePa !== undefined) rows.push(['summary', 'casimir_pressure', observable.casimirPressurePa, 'Pa', 'ideal parallel plates']);
  if (observable.casimirEnergyPerAreaJPerM2 !== undefined) rows.push(['summary', 'casimir_energy_per_area', observable.casimirEnergyPerAreaJPerM2, 'J/m^2', 'ideal parallel plates']);
  latestFrame.adapterMetrics.forEach((item) => rows.push(['adapter', item.label, item.value, item.unit, latestFrame.modelId]));
  latestFrame.spectrum.forEach((series) => series.values.forEach((point) => rows.push(['spectrum', series.name, point.y, series.yUnit ?? '', `frequency_hz=${point.x}`])));
  latestFrame.correlationSeries[0]?.values.forEach((point) => rows.push(['correlation', 'normalized_transverse_correlation', point.y, 'dimensionless', `delay_ps=${point.x}`]));
  const csv = rows.map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(',')).join('\n');
  announce('Scientific CSV export prepared.');
  downloadBlob('qvfv-phase7-data.csv', new Blob([csv], { type: 'text/csv;charset=utf-8' }));
};

function exportImage(): void {
  const url = fieldScene.snapshot();
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'qvfv-phase7-threejs-snapshot.png';
  anchor.click();
  announce('Visualization snapshot export prepared.');
}

const copyInspection = async (): Promise<void> => {
  const text = JSON.stringify(scenarioManifest(), null, 2);
  await navigator.clipboard.writeText(text);
  showInfo('Data Copied', '<p>The current scenario, SI observables, assumptions, source identifiers, and validation metadata were copied to the clipboard.</p>');
};

const wait = (milliseconds: number): Promise<void> => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const setReportStage = (index: number, statusText: string): void => {
  const progress = qs<HTMLElement>('#report-progress');
  const steps = qsa<HTMLLIElement>('#report-steps li');
  const status = qs('#report-status');
  steps.forEach((step, stepIndex) => {
    step.classList.toggle('is-complete', stepIndex < index);
    step.classList.toggle('is-active', stepIndex === index);
  });
  progress.style.width = `${Math.min(100, Math.max(0, (index / steps.length) * 100))}%`;
  status.textContent = statusText;
};

const canvasToJpeg = (canvas: HTMLCanvasElement, quality = 0.86): string => {
  const output = document.createElement('canvas');
  output.width = Math.max(1, canvas.width);
  output.height = Math.max(1, canvas.height);
  const context = output.getContext('2d');
  if (!context) throw new Error('Unable to create report image canvas.');
  context.fillStyle = '#041426';
  context.fillRect(0, 0, output.width, output.height);
  context.drawImage(canvas, 0, 0);
  return output.toDataURL('image/jpeg', quality);
};

const dataUrlToJpeg = async (dataUrl: string, quality = 0.88): Promise<string> => {
  const image = new Image();
  image.decoding = 'async';
  image.src = dataUrl;
  await image.decode();
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, image.naturalWidth);
  canvas.height = Math.max(1, image.naturalHeight);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Unable to convert visualization image for the report.');
  context.fillStyle = '#041426';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0);
  return canvas.toDataURL('image/jpeg', quality);
};

const generatePdfReport = async (): Promise<void> => {
  const dialog = qs<HTMLDialogElement>('#report-dialog');
  const button = qs<HTMLButtonElement>('#generate-pdf');
  const steps = qsa<HTMLLIElement>('#report-steps li');
  const progress = qs<HTMLElement>('#report-progress');
  steps.forEach((step) => step.classList.remove('is-active', 'is-complete'));
  progress.style.width = '0%';
  button.disabled = true;
  if (!dialog.open) dialog.showModal();
  try {
    setReportStage(0, 'Validating the scenario schema and scientific frame.');
    const manifest = scenarioManifest();
    await wait(80);

    setReportStage(1, 'Recalculating authoritative report values from the deterministic kernel.');
    const state = store.getState();
    const reportFrame = calculateScientificFrame(state, simulationTime);
    await wait(80);

    setReportStage(2, 'Capturing the Three.js field viewport.');
    const fieldImage = await dataUrlToJpeg(fieldScene.snapshot());

    setReportStage(3, 'Capturing synchronized analytical figures at report resolution.');
    const plotIds = ['plot-spectrum', 'plot-quadrature', 'plot-correlation', 'plot-detector', 'plot-slice'];
    const plotImages = Object.fromEntries(plotIds.map((id) => {
      const canvas = qs<HTMLCanvasElement>(`#${id}`);
      return [id.replace('plot-', ''), canvasToJpeg(canvas)];
    }));
    await wait(80);

    setReportStage(4, 'Preparing equations, numerical substitutions, units, and validation notes.');
    const equationPayload = {
      modelId: reportFrame.modelId,
      equations: reportFrame.equations,
      adapterMetrics: reportFrame.adapterMetrics,
      centerFrequencyHz: reportFrame.frequencyBand.centerHz,
      bandwidthHz: reportFrame.frequencyBand.bandwidthHz,
      validation: reportFrame.validation,
      uncertainty: reportFrame.uncertainty,
      academicReview: reportFrame.academicReview
    };

    setReportStage(5, 'Compiling source, model, uncertainty, accessibility, and release audits.');
    const [referenceResponse, modelAuditResponse, accessibilityAuditResponse, releaseAuditResponse] = await Promise.all([
      fetch('./data/references.json', { cache: 'no-store' }),
      fetch('./data/model-audit.json', { cache: 'no-store' }),
      fetch('./data/accessibility-audit.json', { cache: 'no-store' }),
      fetch('./data/release-audit.json', { cache: 'no-store' })
    ]);
    const references = referenceResponse.ok ? await referenceResponse.json() as unknown[] : [];
    const modelAudit = modelAuditResponse.ok ? await modelAuditResponse.json() as unknown : null;
    const accessibilityAudit = accessibilityAuditResponse.ok ? await accessibilityAuditResponse.json() as unknown : null;
    const releaseAudit = releaseAuditResponse.ok ? await releaseAuditResponse.json() as unknown : null;

    setReportStage(6, 'Generating the bordered structured PDF on the server.');
    const response = await fetch('./api/generate-report.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-QVFV-Report': 'phase7' },
      body: JSON.stringify({
        manifest,
        frame: reportFrame,
        equations: equationPayload,
        references,
        modelAudit,
        accessibilityAudit,
        releaseAudit,
        images: { field: fieldImage, plots: plotImages },
        client: { userAgent: navigator.userAgent, viewport: { width: innerWidth, height: innerHeight }, generatedAt: new Date().toISOString() }
      })
    });
    if (!response.ok) {
      const type = response.headers.get('content-type') ?? '';
      const detail = type.includes('application/json') ? JSON.stringify(await response.json()) : await response.text();
      throw new Error(`Report endpoint returned HTTP ${response.status}: ${detail.slice(0, 500)}`);
    }
    const pdf = await response.blob();
    if (pdf.type !== 'application/pdf' && !response.headers.get('content-type')?.includes('application/pdf')) throw new Error('The report endpoint did not return a PDF document.');

    setReportStage(7, 'Finalizing the PDF download and preserving reproducibility metadata.');
    const url = URL.createObjectURL(pdf);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `qvfv-phase7-${state.era}-${Date.now()}.pdf`;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    steps.forEach((step) => step.classList.add('is-complete'));
    progress.style.width = '100%';
    qs('#report-status').innerHTML = `Report complete. The PDF was generated from the same scientific frame used by the viewport, plots, inspector, JSON, and CSV exports. <a class="report-download" href="${url}" download="${anchor.download}">Download again</a>`;
  } catch (error) {
    setReportStage(0, 'Report generation stopped.');
    qs('#report-status').textContent = error instanceof Error ? error.message : 'Unknown report generation error.';
  } finally {
    button.disabled = false;
  }
};

const showInfo = (title: string, html: string): void => {
  qs('#info-title').textContent = title;
  qs('#info-content').innerHTML = html;
  qs<HTMLDialogElement>('#info-dialog').showModal();
};

const escapeHtml = (value: string): string => value.replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char] ?? char));

const showMethod = (): void => {
  const frame = latestFrame;
  showInfo('Phase 7 Methodological Note', `
    <p><strong>Active model:</strong> ${escapeHtml(frame.modelId)} version ${escapeHtml(frame.modelVersion)}.</p>
    <p><strong>Scope:</strong> ${escapeHtml(frame.validation.scope)}</p>
    <p><strong>Rendering contract:</strong> ${escapeHtml(frame.renderingMapping)}</p>
    <p><strong>Academic review:</strong> ${escapeHtml(frame.academicReview?.status ?? 'pending')} (${escapeHtml(frame.academicReview?.auditId ?? 'no audit identifier')}).</p>
    <p><strong>Quantified-component uncertainty:</strong> ${escapeHtml(frame.uncertainty ? `${frame.uncertainty.combinedQuantifiedRelative.toExponential(3)} relative` : 'not available')}.</p>
    <p>${escapeHtml(frame.uncertainty?.coverage ?? 'No uncertainty coverage statement is available.')}</p>
    <p><strong>Assumptions:</strong></p><ul>${frame.validation.assumptions.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>
    <p><strong>Known model limitations:</strong></p><ul>${(frame.academicReview?.limitations ?? []).map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>
    <p>Visual gain, animation speed, camera state, glow, and the virtual-particle metaphor do not change exported scientific values.</p>
  `);
};

const showDerivation = (): void => {
  const frame = latestFrame;
  showInfo('Phase 7 Equation Set', `
    <p><strong>${escapeHtml(frame.modelId)}</strong> uses the following declared equations:</p>
    ${frame.equations.map((item) => `<h3>${escapeHtml(item.label)}</h3><p class="typeset-equation">${item.html}</p><p><code>${escapeHtml(item.plain)}</code></p><p>Sources: ${escapeHtml(item.sourceIds.join(', '))}</p>`).join('')}
    <p><strong>Validation:</strong> ${escapeHtml(frame.validation.status)}. ${escapeHtml(frame.validation.scope)}</p>
  `);
};

const loadJson = async <T>(path: string): Promise<T> => {
  const response = await fetch(path, { cache: 'no-store' });
  if (!response.ok) throw new Error(`${path} returned HTTP ${response.status}.`);
  return await response.json() as T;
};

const showReferences = async (): Promise<void> => {
  try {
    const references = await loadJson<Array<{ authors: string; year: number; title: string; container: string; doi?: string; canonicalUrl?: string; role: string; reviewStatus?: string; reviewNote?: string }>>('./data/references.json');
    const rows = references.map((reference) => {
      const identifier = reference.canonicalUrl
        ? `<a href="${escapeHtml(reference.canonicalUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(reference.doi ?? 'Canonical record')}</a>`
        : escapeHtml(reference.doi ?? 'No DOI recorded');
      return `<tr><td>${escapeHtml(String(reference.year))}</td><td><strong>${escapeHtml(reference.title)}</strong><br>${escapeHtml(reference.authors)}<br><span>${escapeHtml(reference.container)}</span></td><td>${identifier}<br><span class="audit-status">${escapeHtml(reference.reviewStatus ?? 'review pending')}</span><br><small>${escapeHtml(reference.role)}</small><br><small>${escapeHtml(reference.reviewNote ?? '')}</small></td></tr>`;
    }).join('');
    showInfo('Phase 7 Academic Source Registry', `<p class="audit-warning">The registry records an internal source and identifier audit. It does not claim independent external peer review. Entries marked for publisher recheck remain visible rather than being silently upgraded.</p><table><thead><tr><th>Year</th><th>Source</th><th>Identifier, role, and review</th></tr></thead><tbody>${rows}</tbody></table>`);
  } catch (error) {
    showInfo('Reference Registry Error', `<p>${escapeHtml(error instanceof Error ? error.message : 'Unable to load the registry.')}</p>`);
  }
};

const showModelAudit = async (): Promise<void> => {
  try {
    const audit = await loadJson<{ scope: string; models: Array<{ id: string; auditId: string; classification: string; reviewStatus: string; implementedClaims: string[]; benchmarkIds: string[]; sourceIds: string[]; uncertaintyScope: string; releaseDecision: string }> }>('./data/model-audit.json');
    const active = audit.models.find((model) => model.id === latestFrame.modelId);
    if (!active) throw new Error(`No model audit is registered for ${latestFrame.modelId}.`);
    showInfo('Active Model Academic Audit', `
      <p class="audit-warning">${escapeHtml(audit.scope)}</p>
      <dl class="audit-grid">
        <dt>Model</dt><dd>${escapeHtml(active.id)}</dd>
        <dt>Audit identifier</dt><dd>${escapeHtml(active.auditId)}</dd>
        <dt>Classification</dt><dd><span class="audit-status">${escapeHtml(active.classification)}</span></dd>
        <dt>Review status</dt><dd>${escapeHtml(active.reviewStatus)}</dd>
        <dt>Release decision</dt><dd>${escapeHtml(active.releaseDecision)}</dd>
        <dt>Benchmarks</dt><dd>${escapeHtml(active.benchmarkIds.join(', '))}</dd>
        <dt>Sources</dt><dd>${escapeHtml(active.sourceIds.join(', '))}</dd>
        <dt>Uncertainty scope</dt><dd>${escapeHtml(active.uncertaintyScope)}</dd>
      </dl>
      <h3>Implemented claims</h3><ul>${active.implementedClaims.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>
    `);
  } catch (error) {
    showInfo('Model Audit Error', `<p>${escapeHtml(error instanceof Error ? error.message : 'Unable to load the model audit.')}</p>`);
  }
};

const showReleaseAudit = async (): Promise<void> => {
  try {
    const audit = await loadJson<{ applicationVersion: string; scenarioSchema: string; designation: string; reviewedOn: string; gates: Record<string, string> }>('./data/release-audit.json');
    const gates = Object.entries(audit.gates).map(([name, status]) => `<dt>${escapeHtml(name.replaceAll(/([A-Z])/g, ' $1'))}</dt><dd>${escapeHtml(status)}</dd>`).join('');
    showInfo('Phase 7 Release Audit', `<p class="audit-warning">${escapeHtml(audit.designation)}</p><dl class="audit-grid"><dt>Application</dt><dd>${escapeHtml(audit.applicationVersion)}</dd><dt>Scenario schema</dt><dd>${escapeHtml(audit.scenarioSchema)}</dd><dt>Reviewed</dt><dd>${escapeHtml(audit.reviewedOn)}</dd>${gates}</dl>`);
  } catch (error) {
    showInfo('Release Audit Error', `<p>${escapeHtml(error instanceof Error ? error.message : 'Unable to load the release audit.')}</p>`);
  }
};

const showAccessibilityAudit = async (): Promise<void> => {
  try {
    const audit = await loadJson<{ standard: string; target: string; status: string; implemented: string[]; manualReviewRequired: string[]; knownLimitations: string[] }>('./data/accessibility-audit.json');
    showInfo('Accessibility Audit', `
      <p><strong>Standard:</strong> ${escapeHtml(audit.standard)}; ${escapeHtml(audit.target)}.</p>
      <p><span class="audit-status">${escapeHtml(audit.status)}</span></p>
      <h3>Implemented</h3><ul>${audit.implemented.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>
      <h3>Manual review still required</h3><ul>${audit.manualReviewRequired.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>
      <h3>Known limitations</h3><ul>${audit.knownLimitations.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>
    `);
  } catch (error) {
    showInfo('Accessibility Audit Error', `<p>${escapeHtml(error instanceof Error ? error.message : 'Unable to load the accessibility audit.')}</p>`);
  }
};

const showKeyboardHelp = (): void => {
  showInfo('Keyboard Controls', `
    <div class="keyboard-grid">
      <kbd>P</kbd><span>Pause or resume animation.</span>
      <kbd>S</kbd><span>Advance one display step while paused or running.</span>
      <kbd>1–3</kbd><span>Select Explore, Learn, or Laboratory.</span>
      <kbd>[ / ]</kbd><span>Move to the previous or next scientific era.</span>
      <kbd>?</kbd><span>Open this keyboard guide.</span>
      <kbd>Esc</kbd><span>Close the active dialog.</span>
      <kbd>← / →</kbd><span>Adjust the selected frequency, angle, or horizontal coordinate when an interactive plot has focus.</span>
      <kbd>A / B</kbd><span>Select Probe A or B when the field snapshot has focus.</span>
    </div>
  `);
};

const handleGlobalKeyboard = (event: KeyboardEvent): void => {
  const target = event.target as HTMLElement | null;
  if (target?.matches('input, select, textarea, [contenteditable="true"]')) return;
  const key = event.key.toLowerCase();
  if (key === '?' || (event.shiftKey && event.key === '/')) {
    event.preventDefault();
    showKeyboardHelp();
    return;
  }
  if (key === 'p') {
    event.preventDefault();
    qs<HTMLButtonElement>('#animation-toggle').click();
    return;
  }
  if (key === 's') {
    event.preventDefault();
    qs<HTMLButtonElement>('#animation-step').click();
    return;
  }
  if (['1', '2', '3'].includes(key)) {
    event.preventDefault();
    const modes: ProductMode[] = ['explore', 'learn', 'laboratory'];
    qsa<HTMLButtonElement>('[data-product-mode]').find((button) => button.dataset.productMode === modes[Number(key) - 1])?.click();
    return;
  }
  if (event.key === '[' || event.key === ']') {
    event.preventDefault();
    const state = store.getState();
    const index = ERA_MANIFESTS.findIndex((era) => era.id === state.era);
    const direction = event.key === ']' ? 1 : -1;
    const next = ERA_MANIFESTS[(index + direction + ERA_MANIFESTS.length) % ERA_MANIFESTS.length];
    qsa<HTMLButtonElement>('.era-button').find((button) => button.dataset.era === next?.id)?.click();
  }
};

const ticker = (timestamp: number): void => {
  const state = store.getState();
  const delta = lastTickerTimestamp ? Math.min(0.1, (timestamp - lastTickerTimestamp) / 1000) : 0;
  lastTickerTimestamp = timestamp;
  const reducedFactor = state.visualization.reducedMotion ? 0.2 : 1;
  if (!state.visualization.animationPaused) simulationTime += delta * state.visualization.animationSpeed * reducedFactor;
  if (pendingSimulationStep > 0) {
    simulationTime += pendingSimulationStep;
    pendingSimulationStep = 0;
  }
  const mobile = matchMedia('(max-width: 760px)').matches;
  const updateInterval = mobile ? 420 : 260;
  if (timestamp - lastFrameUpdate > updateInterval) {
    latestFrame = calculateScientificFrame(state, simulationTime);
    fieldScene.update(state, latestFrame);
    miniPlots.update(latestFrame, state);
    renderInspection(state, latestFrame);
    lastFrameUpdate = timestamp;
  }
  requestAnimationFrame(ticker);
};

renderEraSelector();
bindControls();
window.addEventListener('keydown', handleGlobalKeyboard);
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
reducedMotionQuery.addEventListener?.('change', (event) => store.update((draft) => { draft.visualization.reducedMotion = event.matches; }));
store.subscribe((state) => renderAll(state));
renderAll(store.getState());
requestAnimationFrame(ticker);

window.addEventListener('resize', () => miniPlots.update(latestFrame, store.getState()));
window.addEventListener('beforeunload', () => {
  miniPlots.dispose();
  fieldScene.dispose();
});
